import React, { ReactElement, useMemo, useCallback } from "react";
import { DaysSelectorProps } from "./DaysSelector.types";
import { UnstyledButton } from "src/ui";
import { SpintrTypes } from "src/typings";
import { localize } from "src/l10n";
import { CaptionBody } from "../Text";
import { FormControl } from "src/ui/components/Forms";

function getDayLabels(): { [key: number]: string } {
    let labels: { [key: number]: string } = {};

    labels[SpintrTypes.DaysOfWeek.Monday] = localize("DayOfWeek1");
    labels[SpintrTypes.DaysOfWeek.Tuesday] = localize("DayOfWeek2");
    labels[SpintrTypes.DaysOfWeek.Wednesday] = localize("DayOfWeek3");
    labels[SpintrTypes.DaysOfWeek.Thursday] = localize("DayOfWeek4");
    labels[SpintrTypes.DaysOfWeek.Friday] = localize("DayOfWeek5");
    labels[SpintrTypes.DaysOfWeek.Saturday] = localize("DayOfWeek6");
    labels[SpintrTypes.DaysOfWeek.Sunday] = localize("DayOfWeek7");

    return labels;
}

/**
 * DaysSelector component
 *
 * @param {DaysSelectorProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function DaysSelector({
    firstDayOfWeek = SpintrTypes.DaysOfWeek.Monday,
    label,
    multiple = true,
    onChange,
    value,
}: DaysSelectorProps): ReactElement | null {
    const weekDays = useMemo(() => {
        const days = Array.from({ length: 7 }, (_, i) => 1 << i)

        while (days[0] !== firstDayOfWeek) {
            days.push(days.shift()!);
        }

        return days;
    }, [firstDayOfWeek]);

    const dayLabels =  useMemo(getDayLabels, []);

    const renderDay = useCallback(
        (day: SpintrTypes.DaysOfWeek, index: number) => {
            const isSelected = (multiple && (value & day) === day) || (!multiple && value === day);
            const label = dayLabels[day].substr(0, 2).toUpperCase();

            const className = `DaysSelector-day ${isSelected ? 'selected' : ''}`;

            const onClick = () => {
                if (multiple) {
                    onChange(value ^ day); // Toggle selection
                } else {
                    onChange(isSelected ? 0 : day); // Set or clear the value
                }
            };

            return (
                <UnstyledButton className={className}
                                key={index}
                                onClick={onClick}>

                    <CaptionBody className="DaysSelector-dayLabel"
                                 color={isSelected ? "white" : "contentNormal"}>
                        {label}
                    </CaptionBody>
                </UnstyledButton>
            );
        }, [value]);

    return (
        <FormControl className="DaysSelector" label={label}>
            {weekDays.map((renderDay))}
        </FormControl>
    );
}

export default DaysSelector;

